import { useState, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { v4 as uuidv4 } from 'uuid';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { uploadFile, getCurrentExchangeRate } from "@/integrations/supabase/services";
import { createShiftPayment } from "@/integrations/supabase/shift-services";
import { getBanks } from "@/integrations/supabase/bank-services";
import { ShiftWithRelations } from "@/types/models";
import FileUpload from "@/components/common/FileUpload";
import { useQuery } from "@tanstack/react-query";
import { calculateShiftOutstandingBalance, calculateTotalInReview } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";

const formSchema = z.object({
  bank_id: z.coerce.number().min(1, "Debe seleccionar un banco"),
  amount: z.coerce.number().min(0.01, "El monto debe ser mayor a 0"),
  reference: z.string().min(1, "La referencia es requerida"),
});

type PayShiftFormValues = z.infer<typeof formSchema>;

interface PayShiftModalProps {
  shift: ShiftWithRelations | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const PayShiftModal = ({ shift, isOpen, onClose, onSuccess }: PayShiftModalProps) => {
  const { profile } = useAuth();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [receiptFile, setReceiptFile] = useState<File | null>(null);

  const { data: currentRate } = useQuery({
    queryKey: ['currentExchangeRate'],
    queryFn: getCurrentExchangeRate,
  });

  const { data: banks = [], isLoading: banksLoading } = useQuery({
    queryKey: ['banks'],
    queryFn: getBanks,
    enabled: isOpen,
  });

  const inReviewAmount = useMemo(() => {
    if (!shift || !isOpen) return 0;
    return calculateTotalInReview(shift.payments);
  }, [shift, isOpen]);

  const outstandingBalance = useMemo(() => {
    if (!shift || !isOpen) return 0;

    let balance = calculateShiftOutstandingBalance(shift);
    balance = Math.abs(balance) - inReviewAmount;

    return balance > 0 ? parseFloat(balance.toFixed(2)) : 0;
  }, [shift, inReviewAmount, isOpen]);

  const form = useForm<PayShiftFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      bank_id: 0,
      amount: outstandingBalance,
      reference: "",
    },
  });

  useEffect(() => {
    if (isOpen) {
      form.reset({
        bank_id: 0,
        amount: outstandingBalance,
        reference: "",
      });
      setReceiptFile(null);
    }
  }, [isOpen, outstandingBalance, form]);

  const watchedAmount = form.watch("amount");
  const exchangeRate = currentRate && typeof currentRate === 'object' && 'usd_to_bs' in currentRate ? currentRate.usd_to_bs : 0;
  const amountInBs = watchedAmount && exchangeRate ? Math.ceil(watchedAmount * exchangeRate - 0.00001) : 0;

  const onSubmit = async (data: PayShiftFormValues) => {
    if (!shift || !receiptFile || !exchangeRate) {
      toast({
        variant: "destructive",
        title: "Error",
        description: !receiptFile ? "Debe subir el comprobante de pago" : "No se pudo obtener la tasa de cambio",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const fileReader = new FileReader();
      const base64String = await new Promise<string>((resolve) => {
        fileReader.onload = () => resolve(fileReader.result as string);
        fileReader.readAsDataURL(receiptFile);
      });

      const filePath = `receipts/${uuidv4()}.jpg`;
      const receiptUrl = await uploadFile(base64String, filePath);

      const branchId = profile.branch_id || shift?.operator?.branch_id || shift?.operator?.branch?.id;

      if (!branchId) {
        throw new Error("No se pudo determinar la sucursal para el pago");
      }

      await createShiftPayment({
        shift_id: shift.id,
        operator_id: profile.id,
        branch_id: branchId,
        bank_id: data.bank_id,
        amount: data.amount,
        reference: data.reference,
        exchange_rate: exchangeRate,
        receipt_photo_url: receiptUrl,
      });
      
      toast({
        title: "Pago registrado para revisión",
        description: "El pago ha sido enviado y está pendiente de aprobación por un administrador.",
      });

      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error al registrar el pago:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Hubo un error al registrar el pago",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    form.reset();
    setReceiptFile(null);
    onClose();
  };

  if (banksLoading) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Registrar Pago de Jornada</DialogTitle>
          </DialogHeader>
          <div className="flex justify-center py-8">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Registrar Pago de Jornada</DialogTitle>
          <DialogDescription>
            Registra el pago de la jornada seleccionada. El pago quedará pendiente de revisión.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="bank_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Banco</FormLabel>
                  <Select 
                    onValueChange={(value) => field.onChange(Number(value))} 
                    value={field.value.toString()}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar banco" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {banks.map((bank) => (
                        <SelectItem key={bank.id} value={bank.id.toString()}>
                          {bank.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="text-sm">
              <p>Deuda Total: <span className="font-semibold">${(outstandingBalance + inReviewAmount).toFixed(2)}</span></p>
              {inReviewAmount > 0 && (
                <p className="text-amber-600">
                  Pagos en Revisión: <span className="font-semibold">-${inReviewAmount.toFixed(2)}</span>
                </p>
              )}
              <p className="text-gray-600 font-bold">
                Saldo Pendiente: <span className="font-semibold">${outstandingBalance.toFixed(2)}</span>
              </p>
            </div>

            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Monto (USD)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      {...field}
                      onChange={(e) => {
                        const value = e.target.value.replace(/(\.\d{2})\d+/, '$1');
                        field.onChange(value === '' ? 0 : Number(value));
                      }}
                    />
                  </FormControl>
                  {watchedAmount > 0 && exchangeRate > 0 && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {amountInBs.toLocaleString('es-VE')} Bs. (Tasa: {exchangeRate.toFixed(2)})
                    </p>
                  )}
                  {watchedAmount > outstandingBalance && (
                    <p className="text-sm font-medium text-amber-600 mt-1">
                      ⚠️ Advertencia: El monto ingresado (${watchedAmount.toFixed(2)}) es mayor que el saldo pendiente (${outstandingBalance.toFixed(2)}). Deuda Total:(${(outstandingBalance + inReviewAmount).toFixed(2)}), Pagos en Revisión: (${inReviewAmount.toFixed(2)})
                    </p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="reference"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Referencia</FormLabel>
                  <FormControl>
                    <Input placeholder="Número de referencia" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FileUpload
              label="Comprobante de Pago"
              onFileSelect={setReceiptFile}
              onFileRemove={() => setReceiptFile(null)}
              selectedFile={receiptFile}
              accept="image/*"
              maxSize={5}
            />

            <DialogFooter className="gap-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleClose}
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                disabled={isSubmitting}
              >
                {isSubmitting ? "Enviando para revisión..." : "Enviar para Revisión"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default PayShiftModal;